// TEMP: floating panel to walk the full "Set as Master Integration" journey, one case at a time. Picking a case sets
// the Review (step 1) to that case's notices and step count; pressing Proceed then opens the case's remaining steps in
// order, so the whole journey can be previewed end-to-end from step 1. Delete this file (and its use in
// switchSalesChannelDialog.tsx) before merging.
import React, { FC, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useDialogManager } from 'src/components/widgets/dialog';
import styled from 'styled-components';
import { SwitchTaskCreatedDialog } from './completeSwitchDialog';
import { DisconnectSimulationDialog, DisconnectSimulationVariant } from './disconnectSimulations';
import CustomPlanFinancialPreviewDialog, { CustomPlanFinancialPreview } from './customPlanFinancialPreviewDialog';
import { PlanPreparationFailedDialog, SwitchFailedDialog, SwitchTakingTooLongDialog } from './switchFailureDialogs';
import { SellerPlanCheckInfo, SWITCH_DIALOG_IDS } from './switchTypes';
import { SwitchWizardStepCount } from './switchWizardSteps';

export interface ReviewNoticeFlags {
    masterCurrencyCode?: string | null;
    secondaryCurrencyCode?: string | null;
    requiresShopifyApproval: boolean;
    requiresCustomPlanSubscription: boolean;
    requiresPaymentMethodSetup: boolean;
    hasCurrencyMismatch: boolean;
    /** Whether the new master (secondary) is an API channel — only matters for the API/non-API subscription notice. */
    secondaryIsApi?: boolean;
}

/** A step shown after the Review: `advance` opens the next step, `close` ends the whole simulation (and the host dialog). */
type FollowUpStep = (advance: () => void, close: () => void) => React.ReactElement;

/**
 * Drives the host Review into one of its "cannot proceed" branches without any real backend data: a store that cannot
 * switch automatically, a legacy plan, blocking products, or a plan product-limit. Any field left undefined falls back
 * to the dialog's real prop.
 */
export interface ReviewStateOverride {
    canSwitch?: boolean;
    planCheck?: SellerPlanCheckInfo;
    hasConfiguratorProducts?: boolean;
    hasPodProducts?: boolean;
}

export interface SimulationCase {
    value: string;
    label: string;
    /** One line describing the whole journey for this case. */
    summary: string;
    /** The real-data case: Proceed runs the actual switch instead of a simulation. */
    isReal?: boolean;
    /** What the Review step shows for this case. */
    noticeFlags: ReviewNoticeFlags;
    stepCount: SwitchWizardStepCount;
    /** Steps 2..N, opened in order once the seller proceeds from the Review. Empty when the case cannot proceed. */
    followUp: FollowUpStep[];
    /** Set for "cannot proceed" cases: reshapes the Review so its blocking branch renders. */
    reviewOverride?: ReviewStateOverride;
    /** Set for error cases: clicking opens this dialog directly over the Review instead of reconfiguring it. */
    openModal?: (close: () => void) => React.ReactElement;
}

// A believable financial-preview payload for the step-2 simulations. warnings[0] and warnings[last] are dropped by the
// dialog (it renders warnings.slice(1, -1)), so they are padded with empty strings.
const makeMockPreview = (newCurrencyCode: string): CustomPlanFinancialPreview => ({
    currentPlan: { billingCyclePrice: 2038.92, billingPeriod: 1, billingPeriodUnit: 'Year', currencyCode: 'EUR' },
    newPlan: { billingCyclePrice: 2908.03, billingPeriod: 1, billingPeriodUnit: 'Year', currencyCode: newCurrencyCode },
    proration: { available: true, remainingDays: 12 },
    paymentOnPlanChange: {
        estimatedCreditApplied: 0,
        estimatedRefundValue: 2905.27,
        currencyCode: newCurrencyCode,
        nextRenewalAtUtc: '2027-09-25T00:00:00Z',
        nextRenewalAmount: 2908.03
    },
    warnings: ['']
});

/**
 * Every case the "Set as Master Integration" flow can take, each as a complete journey. The channel names are baked in
 * so the simulated steps 2 & 3 read like the real thing. 'real' is the only case with no follow-up: it proceeds through
 * the actual switch.
 */
export const getSimulationCases = (
    masterSalesChannelName: string,
    secondarySalesChannelName: string,
    realFlags: ReviewNoticeFlags,
    realStepCount: SwitchWizardStepCount
): SimulationCase[] => {
    const channelNames = { masterSalesChannelName, secondarySalesChannelName };

    const financialPreviewStep =
        (preview: CustomPlanFinancialPreview, paymentMethodSetupRequired: boolean, stepCount: SwitchWizardStepCount): FollowUpStep =>
        (advance, close) => (
            <CustomPlanFinancialPreviewDialog
                preview={preview}
                paymentMethodSetupRequired={paymentMethodSetupRequired}
                stepCount={stepCount}
                onContinue={advance}
                onClose={close}
            />
        );

    // Last step of every journey: the "switch task created" screen confirming the switch started, with the link to the task.
    const finalSteps = (stepCount: SwitchWizardStepCount): FollowUpStep[] => [
        (_advance, close) => <SwitchTaskCreatedDialog taskID={12345} {...channelNames} stepCount={stepCount} onGoToTask={close} onClose={close} />
    ];

    // The four switch scenarios, in the taxonomy order (see canSwitchSalesChannel + switchSalesChannelDialog's flags):
    // 1) Shopify ↔ Shopify, 2) non-Shopify same type & currency, 3) non-Shopify same type, currency differs,
    // 4) non-Shopify ↔ API. A valid switch is only ever bothShopify or neitherShopify.
    return [
        {
            value: 'real',
            label: 'Real data',
            summary: 'Uses the stores’ real flags; Proceed runs the actual switch.',
            isReal: true,
            noticeFlags: realFlags,
            stepCount: realStepCount,
            followUp: []
        },
        {
            // 1) Two Shopify stores: the subscription is recreated on the new master and approved on Shopify (external).
            value: 'shopifyApproval',
            label: '1 · Shopify → Shopify',
            summary: 'Both Shopify · approval on Shopify required (3 steps)',
            noticeFlags: {
                ...realFlags,
                requiresShopifyApproval: true,
                requiresCustomPlanSubscription: false,
                requiresPaymentMethodSetup: false,
                hasCurrencyMismatch: false
            },
            stepCount: 3,
            // Step 2 is an external Shopify redirect with no dialog of its own, so the simulation jumps to the final steps.
            followUp: finalSteps(3)
        },
        {
            // 2) Two non-Shopify of the same integration type and the same currency: nothing to subscribe.
            value: 'noNotices',
            label: '2 · Non-Shopify, same currency',
            summary: 'Same integration & currency · no new plan (2 steps)',
            noticeFlags: {
                ...realFlags,
                requiresShopifyApproval: false,
                requiresCustomPlanSubscription: false,
                requiresPaymentMethodSetup: false,
                hasCurrencyMismatch: false
            },
            stepCount: 2,
            followUp: finalSteps(2)
        },
        {
            // 3) Two non-Shopify of the same type but different currency: a new plan is needed, handled internally (no checkout).
            value: 'customPlanCurrency',
            label: '3 · Non-Shopify, currency change',
            summary: 'New plan, no checkout (currency differs) · 3 steps',
            noticeFlags: {
                masterCurrencyCode: 'EUR',
                secondaryCurrencyCode: 'USD',
                requiresShopifyApproval: false,
                requiresCustomPlanSubscription: true,
                requiresPaymentMethodSetup: false,
                hasCurrencyMismatch: true
            },
            stepCount: 3,
            followUp: [financialPreviewStep(makeMockPreview('USD'), false, 3), ...finalSteps(3)]
        },
        {
            // 4) Non-Shopify ↔ API: different integration types always require a new plan and an external checkout,
            // regardless of currency.
            value: 'paymentMethod',
            label: '4 · Non-Shopify ↔ API',
            summary: 'New plan + checkout (payment method) · 3 steps',
            noticeFlags: {
                ...realFlags,
                requiresShopifyApproval: false,
                requiresCustomPlanSubscription: true,
                requiresPaymentMethodSetup: true,
                hasCurrencyMismatch: false,
                // The new master in this case is the API channel, so the subscription notice reads "an API channel".
                secondaryIsApi: true
            },
            stepCount: 3,
            followUp: [financialPreviewStep(makeMockPreview('EUR'), true, 3), ...finalSteps(3)]
        }
    ];
};

// Neutral flags for a case that never reaches the notices section (blocking branches render instead) or is covered by an overlay.
const NEUTRAL_FLAGS: ReviewNoticeFlags = {
    requiresShopifyApproval: false,
    requiresCustomPlanSubscription: false,
    requiresPaymentMethodSetup: false,
    hasCurrencyMismatch: false
};

/**
 * The dedicated list of dead ends, kept apart from the journeys above: the cases where the switch cannot proceed (shown
 * as a reshaped Review) and the error modals that can surface during the flow (shown directly over the Review). None of
 * these has a follow-up — there is nowhere to proceed to.
 */
export const getBlockedSimulationCases = (
    masterSalesChannelName: string,
    secondarySalesChannelName: string,
    realStepCount: SwitchWizardStepCount
): SimulationCase[] => {
    const channelNames = { masterSalesChannelName, secondarySalesChannelName };
    const blocked = (value: string, label: string, summary: string, reviewOverride: ReviewStateOverride): SimulationCase => ({
        value,
        label,
        summary,
        noticeFlags: NEUTRAL_FLAGS,
        stepCount: realStepCount,
        followUp: [],
        reviewOverride
    });
    const errorModal = (value: string, label: string, summary: string, render: (close: () => void) => React.ReactElement): SimulationCase => ({
        value,
        label,
        summary,
        noticeFlags: NEUTRAL_FLAGS,
        stepCount: realStepCount,
        followUp: [],
        openModal: render
    });

    const blockedCases: SimulationCase[] = [
        blocked('switchUnavailable', 'Switch unavailable (manual migration)', 'Different platforms — only a manual migration is possible.', {
            canSwitch: false
        }),
        blocked('legacyPlan', 'Legacy V1 plan', 'The current plan cannot switch at all; Contact support only.', {
            planCheck: {
                isLegacyPlan: true,
                isV1Plan: true,
                isProductLimitReached: false,
                maxProductsGrow: null,
                maxProductsScale: null,
                currentProductCount: 0
            }
        }),
        blocked('podProducts', 'Print-on-Demand products present', 'POD products must be removed first; Proceed stays disabled.', { hasPodProducts: true }),
        blocked('configuratorProducts', '3D Configurator products present', '3D Configurator products must be removed first; Proceed stays disabled.', {
            hasConfiguratorProducts: true
        }),
        blocked('bothBlockingProducts', 'POD + 3D Configurator products', 'Both blockers at once; Proceed stays disabled.', {
            hasPodProducts: true,
            hasConfiguratorProducts: true
        }),
        blocked('productLimit', 'Product limit reached', 'Published products exceed the plan limit; Proceed stays disabled.', {
            planCheck: {
                isLegacyPlan: false,
                isV1Plan: false,
                isProductLimitReached: true,
                maxProductsGrow: null,
                maxProductsScale: 500,
                currentProductCount: 650
            }
        }),
        errorModal('planPreparationFailed', 'Error — plan preparation failed', 'The custom-plan task failed, so the switch never started.', (close) => (
            <PlanPreparationFailedDialog
                {...channelNames}
                errorDetail='CreateCustomPlanForSeller task failed: plan template not found (code 4042).'
                onClose={close}
            />
        )),
        errorModal('switchFailed', 'Error — switch failed', 'A task failed after the switch started; some products may be half-migrated.', (close) => (
            <SwitchFailedDialog {...channelNames} errorDetail='ProductSwitcher task 12345 stopped at 62%: timeout migrating product #9981.' onClose={close} />
        )),
        errorModal('switchTakingTooLong', 'Warning — switch taking too long', 'Polling has run over 2 hours; support needs to look at it.', (close) => (
            <SwitchTakingTooLongDialog {...channelNames} onClose={close} />
        ))
    ];

    // Progressive number on each label, in the same "N · " style as the journeys tab.
    return blockedCases.map((simulationCase, index) => ({ ...simulationCase, label: `${index + 1} · ${simulationCase.label}` }));
};

/**
 * Every state the "Disconnect sales channel" modal can take, each opened directly as an overlay over the Review (like the
 * error cases). The modals are driven by mock data — see disconnectSimulations.tsx.
 */
export const getDisconnectSimulationCases = (storeName: string, storeDomain: string | undefined, realStepCount: SwitchWizardStepCount): SimulationCase[] => {
    const disconnectCase = (value: string, label: string, summary: string, variant: DisconnectSimulationVariant): SimulationCase => ({
        value,
        label,
        summary,
        noticeFlags: NEUTRAL_FLAGS,
        stepCount: realStepCount,
        followUp: [],
        openModal: (close) => <DisconnectSimulationDialog variant={variant} storeName={storeName} storeDomain={storeDomain} onClose={close} />
    });

    // Platform shapes which flow runs: Shopify/BigCommerce disconnect by uninstalling the app (no footer), every other
    // platform disconnects directly (destructive footer). Labels lead with the platform, like the journeys tab.
    const cases = [
        disconnectCase('disc-secondaryConfirm', 'Non-Shopify secondary — confirm', 'Direct disconnect, nothing to delete', 'secondaryConfirm'),
        disconnectCase('disc-secondaryMany', 'Non-Shopify secondary — products deleted', 'Danger banner + "link your products" step', 'secondaryMany'),
        disconnectCase('disc-masterBreak', 'Non-Shopify master — products will break', 'Broken-products list', 'masterBreak'),
        disconnectCase('disc-masterConfirm', 'Non-Shopify master — confirm', 'Simple confirmation for the master', 'masterConfirm'),
        disconnectCase(
            'disc-uninstallSteps',
            'Shopify secondary — uninstall steps',
            'Shopify / BigCommerce · link step + uninstall-app step, no footer',
            'uninstallSteps'
        ),
        disconnectCase(
            'disc-uninstallConfirm',
            'Shopify secondary — uninstall confirm',
            'Shopify / BigCommerce · "remove the app", no footer',
            'uninstallConfirm'
        ),
        disconnectCase('disc-uninstallMaster', 'Shopify master — uninstall', 'Shopify / BigCommerce · uninstall-the-app banner', 'uninstallMaster'),
        disconnectCase('disc-loading', 'Any platform — loading', 'Spinner while affected products load', 'loading')
    ];

    // Progressive number on each label, in the same "N · " style as the other tabs.
    return cases.map((simulationCase, index) => ({ ...simulationCase, label: `${index + 1} · ${simulationCase.label}` }));
};

const SIM_DIALOG_ID = 'switch-preview-sim';

/**
 * Walks a case's follow-up steps through a single dialog slot: each step's primary action advances to the next, and any
 * close ends the whole simulation — dismissing both the simulated step and the step-1 Review that hosts the panel, so
 * the X behaves like exiting the wizard rather than dropping back onto step 1.
 */
export const useSwitchSimulationRunner = () => {
    const { showDialog, closeDialog } = useDialogManager();
    return useCallback(
        (followUp: FollowUpStep[]) => {
            const close = () => {
                closeDialog(SIM_DIALOG_ID);
                closeDialog(SWITCH_DIALOG_IDS.switchSalesChannel);
            };
            const openStep = (index: number) => {
                if (index >= followUp.length) {
                    close();
                    return;
                }
                showDialog(
                    SIM_DIALOG_ID,
                    followUp[index](() => openStep(index + 1), close)
                );
            };
            openStep(0);
        },
        [showDialog, closeDialog]
    );
};

const Panel = styled.div`
    position: fixed;
    left: 16px;
    bottom: 16px;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 280px;
    /* Never grow past the viewport; the case list inside scrolls when it doesn't fit. */
    max-height: calc(100vh - 32px);
    padding: 10px;
    background: #ffffff;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    font-size: 12px;
`;

const PanelTitle = styled.strong`
    font-size: 11px;
    text-transform: uppercase;
    color: #6b6b6b;
`;

const PanelHint = styled.span`
    margin-bottom: 2px;
    font-size: 11px;
    line-height: 15px;
    color: #9b9b9b;
`;

const TabBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
`;

const Tab = styled.button<{ $active?: boolean }>`
    flex: 1 1 auto;
    padding: 5px 8px;
    border: 1px solid ${(props) => (props.$active ? props.theme.app.primary : props.theme.app.borderColor)};
    border-radius: 6px;
    background: ${(props) => (props.$active ? props.theme.app.primary : '#ffffff')};
    color: ${(props) => (props.$active ? '#ffffff' : props.theme.app.textColor)};
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
`;

/** Holds the buttons of the active tab; scrolls on its own so the whole panel stays inside the viewport. */
const CaseList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow-y: auto;
`;

const CaseButton = styled.button<{ $active?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 6px 10px;
    border: 1px solid ${(props) => (props.$active ? props.theme.app.primary : props.theme.app.borderColor)};
    border-radius: 6px;
    background: ${(props) => (props.$active ? props.theme.app.primary : '#ffffff')};
    color: ${(props) => (props.$active ? '#ffffff' : props.theme.app.textColor)};
    text-align: left;
    cursor: pointer;
`;

const CaseLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
`;

const CaseSummary = styled.span<{ $active?: boolean }>`
    font-size: 11px;
    line-height: 14px;
    opacity: 0.75;
`;

export type SimulationTab = 'journeys' | 'blocked' | 'disconnect';

export const SwitchSimulationPanel: FC<{
    cases: SimulationCase[];
    blockedCases: SimulationCase[];
    disconnectCases: SimulationCase[];
    activeValue: string;
    // Controlled by the host so the tab survives the "Switch unavailable" case, which remounts this panel.
    activeTab: SimulationTab;
    onTabChange: (tab: SimulationTab) => void;
    onSelect: (simulationCase: SimulationCase) => void;
}> = ({ cases, blockedCases, disconnectCases, activeValue, activeTab, onTabChange, onSelect }) => {
    const { showDialog, closeDialog } = useDialogManager();

    const handleSelect = (simulationCase: SimulationCase) => {
        // Dismiss any step still open from the flow the seller was in.
        closeDialog(SIM_DIALOG_ID);
        if (simulationCase.openModal) {
            // Error cases preview a single dialog over the current Review; closing it just dismisses the overlay, so the
            // panel stays and another case can be tried. The selected flow is left untouched.
            showDialog(
                SIM_DIALOG_ID,
                simulationCase.openModal(() => closeDialog(SIM_DIALOG_ID))
            );
            return;
        }
        // Everything else (re)configures the Review and restarts that flow from step 1.
        onSelect(simulationCase);
    };

    const renderButton = (simulationCase: SimulationCase) => (
        <CaseButton key={simulationCase.value} type='button' $active={activeValue === simulationCase.value} onClick={() => handleSelect(simulationCase)}>
            <CaseLabel>{simulationCase.label}</CaseLabel>
            <CaseSummary>{simulationCase.summary}</CaseSummary>
        </CaseButton>
    );

    // Portalled to <body> so the panel escapes the step-1 dialog's overlay stacking context; otherwise a follow-up step
    // opened on top would cover the panel and swallow its clicks.
    return createPortal(
        <Panel>
            <PanelTitle>Simulate a case</PanelTitle>
            <TabBar>
                <Tab type='button' $active={activeTab === 'journeys'} onClick={() => onTabChange('journeys')}>
                    Journeys
                </Tab>
                <Tab type='button' $active={activeTab === 'blocked'} onClick={() => onTabChange('blocked')}>
                    Cannot proceed
                </Tab>
                <Tab type='button' $active={activeTab === 'disconnect'} onClick={() => onTabChange('disconnect')}>
                    Disconnect
                </Tab>
            </TabBar>
            {activeTab === 'journeys' && (
                <>
                    <PanelHint>Pick a case to set this Review step, then Proceed to walk its next steps.</PanelHint>
                    <CaseList>{cases.map(renderButton)}</CaseList>
                </>
            )}
            {activeTab === 'blocked' && (
                <>
                    <PanelHint>Dead ends: a reshaped Review that cannot proceed, or an error modal shown over it.</PanelHint>
                    <CaseList>{blockedCases.map(renderButton)}</CaseList>
                </>
            )}
            {activeTab === 'disconnect' && (
                <>
                    <PanelHint>Every modal the "Disconnect sales channel" flow can show, opened over the Review.</PanelHint>
                    <CaseList>{disconnectCases.map(renderButton)}</CaseList>
                </>
            )}
        </Panel>,
        document.body
    );
};
